import { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import { IntakeContext } from '../context/IntakeContext'
// import { Link } from "react-router-dom";

// @mui material components
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Icon from '@mui/material/Icon'
import Box from '@mui/material/Box'

// Soft UI Dashboard PRO React icons
import AlarmOnIcon from '@mui/icons-material/AlarmOn'
import ScheduleIcon from '@mui/icons-material/Schedule'
import MoreTimeIcon from '@mui/icons-material/MoreTime'
import { format, addYears, addDays, max, min } from 'date-fns'

const StyledMSB = styled.div`
  text-align: center;
  position: relative;
  padding: 16px;
  height: 100%;
  cursor: pointer;
  border: ${(props) =>
    props.active ? '2px solid #a3a7aa' : '2px solid #e5e5e5'};
  background: ${(props) => (props.active ? '#eeeeee' : '#fff')};
  color: #212121;
  border-radius: 0.75rem;
  transition: 0.2s all ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    border: 2px solid #a3a7aa;
    background: #f5f5f5;
  }

  > h6,
  h5 {
    color: #212121;
    letter-spacing: 0.3px;
    font-weight: 600;
    line-height: 1.4;
    margin: 0;
  }

  > span {
    border-radius: 50%;
    height: 1.5em;
    width: 1.5em;
    padding: 8px;
    display: flex;
    justify-content: center;
    align-items: center;

    &.yesIcon {
      color: #4caf50;
      background-color: #dbefdc;
    }
    &.noIcon {
      color: #fd5c70;
      background-color: #fedee2;
    }
  }
`

const PlannedRetirementTileSelector = () => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)

  const eligibilityScenariosFirstFullyEligibleDate = min([
    max([
      addYears(new Date(intakeState?.dateOfHire), 30),
      addYears(new Date(intakeState?.dateOfBirth), 57),
    ]),
    max([
      addYears(new Date(intakeState?.dateOfHire), 20),
      addYears(new Date(intakeState?.dateOfBirth), 60),
    ]),
    max([
      addYears(new Date(intakeState?.dateOfHire), 5),
      addYears(new Date(intakeState?.dateOfBirth), 62),
    ]),
  ])

  const soonestFullyEligibleDate = new Date(
    eligibilityScenariosFirstFullyEligibleDate.getFullYear(),
    eligibilityScenariosFirstFullyEligibleDate.getMonth() + 1,
    0
  )
  const retirementDates = {
    asap: format(
      soonestFullyEligibleDate < new Date()
        ? new Date(new Date().getFullYear(), new Date().getMonth() + 7, 0)
        : soonestFullyEligibleDate - 1,
      'yyyy-MM-dd'
    ),
    nextTwoYears: format(
      min([
        soonestFullyEligibleDate,
        new Date(new Date().getFullYear() + 2, new Date().getMonth(), 0),
      ]) < new Date()
        ? new Date(new Date().getFullYear(), new Date().getMonth() + 7, 0)
        : min([
            soonestFullyEligibleDate,
            new Date(new Date().getFullYear() + 2, new Date().getMonth(), 0),
          ]),
      'yyyy-MM-dd'
    ),
    moreThanTwoYears: format(
      max([soonestFullyEligibleDate, addYears(addDays(new Date(), 1), 2)]),
      'yyyy-MM-dd'
    ),
    notSelected: format(
      soonestFullyEligibleDate < new Date()
        ? new Date(new Date().getFullYear(), new Date().getMonth() + 7, 0)
        : soonestFullyEligibleDate,
      'yyyy-MM-dd'
    ),
  }

  const plannedRetirementHolder =
    intakeState?.dateOfPlannedRetirement === retirementDates.asap
      ? 'asap'
      : intakeState?.dateOfPlannedRetirement === retirementDates.nextTwoYears
      ? 'nextTwoYears'
      : intakeState?.dateOfPlannedRetirement ===
        retirementDates.moreThanTwoYears
      ? 'moreThanTwoYears'
      : 'notSelected'

  const [dateOfPlannedRetirement, setDateOfPlannedRetirement] = useState(
    plannedRetirementHolder
  )

  const toggleDate = (goal) => {
    setDateOfPlannedRetirement(goal)
  }

  useEffect(() => {
    if (typeof retirementDates[dateOfPlannedRetirement] != 'string') return
    updateIntakeStateAtKey({
      dateOfPlannedRetirement: retirementDates[dateOfPlannedRetirement],
    })
  }, [dateOfPlannedRetirement])

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box mb={1}>
        <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
          Tell us about your retirement plan
        </Typography>
      </Box>
      <Typography variant="body1" fontSize={{ xs: '16px', sm: '20px' }}>
        When do you plan on retiring?
      </Typography>
      <Box mt={2}>
        <Grid container spacing={1} justifyContent="center" mb={3}>
          <Grid item xs={4}>
            <StyledMSB
              active={dateOfPlannedRetirement === 'asap'}
              onClick={() => toggleDate('asap')}
            >
              <Icon fontSize="large">
                <AlarmOnIcon />
              </Icon>
              <Typography variant="h6" textTransform="capitalize">
                ASAP
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={4}>
            <StyledMSB
              active={dateOfPlannedRetirement === 'nextTwoYears'}
              onClick={() => toggleDate('nextTwoYears')}
            >
              <Icon fontSize="large">
                <ScheduleIcon />
              </Icon>
              <Typography variant="h6" textTransform="capitalize">
                In the Next 2 Years
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={4}>
            <StyledMSB
              active={dateOfPlannedRetirement === 'moreThanTwoYears'}
              onClick={() => toggleDate('moreThanTwoYears')}
            >
              <Icon fontSize="large">
                <MoreTimeIcon />
              </Icon>
              <Typography variant="h6" textTransform="capitalize">
                2+ Years From Now
              </Typography>
            </StyledMSB>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default PlannedRetirementTileSelector
